import React, { useState, useEffect } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import "../styles/google-map.css";
import { getGymList, getGymLocationData } from "../utilities/firestore/firestoreUtils.js";

const defaultCenter = {
  lat: 39.8283, // Geographic center of the United States (approx)
  lng: -98.5795, // Geographic center of the United States (approx)
};

const GoogleMapComponent = ({ zoom = 4, useUserLocation = true, mapWidth = "100%", mapHeight = "auto", borderRadius }) => {
  const [center, setCenter] = useState(defaultCenter);
  const [userLocation, setUserLocation] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Adjust container style based on mobile and custom map size
  const containerStyle = {
    width: mapWidth, // Use the passed width or default to 100%
    height: mapHeight, // Use the passed height or default to 60vh
    minHeight: isMobile ? "400px" : "auto", // Minimum height on mobile if needed
    bottom: "0",
    borderRadius: isMobile ? "0px 0px 10px 10px" : borderRadius,
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyD_sUj-WZBTyLyklmOWftegTKrVSSFp9kQ", // Replace with your API key
  });

  useEffect(() => {
    if (isLoaded) {
      const fetchData = async () => {
        try {
          const gymNames = await getGymList();
          if (gymNames && gymNames.length > 0) {
            const gymsWithLocationData = await getGymLocationData(gymNames);
            const resolvedMarkers = gymsWithLocationData.map((gym) => {
              const locationData = gym.locationData.gymLocation;
              return {
                position: {
                  lat: parseFloat(locationData._lat),
                  lng: parseFloat(locationData._long),
                },
                name: gym.locationData.gymName,
                address: gym.locationData.gymAddress,
                website: gym.locationData.websiteLink,
                phone: gym.locationData.gymPhone,
              };
            });
            setMarkers(resolvedMarkers);
          } else {
          }
        } catch (error) {
        }
      };

      fetchData();

      if (useUserLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLoc = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCenter(userLoc);
            setUserLocation(userLoc);
          },
          (error) => {
            setCenter(defaultCenter); // Fallback to default center if geolocation fails
          }
        );
      }
    }
  }, [isLoaded, useUserLocation]);

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={zoom}>
      {userLocation && useUserLocation && (
        <Marker
          position={userLocation}
          icon={{ url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png" }}
        />
      )}

      {markers.map((marker, index) => (
        <Marker
          key={index}
          position={marker.position}
          onClick={() => setSelectedMarker(marker)}
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div>
            <h4>{selectedMarker.name}</h4>
            <p>{selectedMarker.address}</p>
            {selectedMarker.website && (
              <p>
                <a href={selectedMarker.website} target="_blank" rel="noopener noreferrer">
                  Visit Website
                </a>
              </p>
            )}
            {selectedMarker.phone && <p>Phone: {selectedMarker.phone}</p>}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default GoogleMapComponent;